import type { ImageAttachment } from "@/components/panels/image-preview-strip";
import { registerOutboundScreensForImages } from "./outbound-screenshot-metadata";
import { globalPointToScreenshotPoint } from "./screenshot-pointer";
import { textChapterId, type TextAttentionState } from "./text-attention-store";

export function textAttentionSendState(
  state: TextAttentionState,
  attachments: ImageAttachment[],
): "empty" | "pending" | "ready" {
  if (state.annotations.length === 0) return "empty";
  const readyIds = new Set(
    attachments
      .filter((item) => !item.uploading && item.imageId)
      .map((item) => item.imageId),
  );
  return state.annotations.every((annotation) => {
    const chapterId = textChapterId(
      annotation.id.split("/")[0]!,
      annotation.displayId,
      annotation.revision,
    );
    const capture = state.captures[chapterId];
    return (
      capture?.status === "ready" &&
      capture.attachment?.imageId &&
      readyIds.has(capture.attachment.imageId)
    );
  })
    ? "ready"
    : "pending";
}

/** Drawn marks are sent as screenshot-local pixels, linked to the attached image order. */
export function appendTextAttentionPrompt(
  message: string,
  state: TextAttentionState,
  attachments: ImageAttachment[],
): string {
  if (state.annotations.length === 0) return message;
  const ready = attachments.filter((item) => !item.uploading && item.imageId);
  const lines = state.annotations.map((annotation) => {
    const chapterId = textChapterId(
      annotation.id.split("/")[0]!,
      annotation.displayId,
      annotation.revision,
    );
    const capture = state.captures[chapterId];
    if (!capture?.screen || !capture.attachment?.imageId) {
      throw new Error(`Annotation ${annotation.number} has no screenshot`);
    }
    const imageNumber =
      ready.findIndex((item) => item.imageId === capture.attachment?.imageId) +
      1;
    if (imageNumber === 0) {
      throw new Error(
        `Annotation ${annotation.number} screenshot is not attached`,
      );
    }
    const strokes = annotation.strokes
      .map((stroke) => {
        const points = stroke
          .filter(
            (_point, index) =>
              index === 0 || index === stroke.length - 1 || index % 8 === 0,
          )
          .flatMap((point) => {
            const pixel = globalPointToScreenshotPoint(capture.screen!, point);
            return pixel ? [`(${pixel.x},${pixel.y})`] : [];
          });
        return points.join(" → ");
      })
      .filter(Boolean);
    const comment = annotation.comment.trim();
    return (
      `${annotation.number}. Attached image ${imageNumber}; strokes ${strokes.join(" / ")}` +
      (comment ? `; comment: ${comment}` : "")
    );
  });
  return [
    message.trim(),
    "[Screen annotations: coordinates are pixels in each attached image]",
    ...lines,
  ]
    .filter(Boolean)
    .join("\n");
}

export function registerTextAttentionScreens(
  state: TextAttentionState,
  attachments: ImageAttachment[],
): void {
  const ready = attachments.filter((item) => !item.uploading && item.imageId);
  const captures = Object.values(state.captures).filter(
    (item) =>
      item.status === "ready" && item.attachment?.imageId && item.screen,
  );
  const ids = captures.map((item) => item.attachment!.imageId!);
  const screens = captures
    .map((item) => ({
      ...item.screen!,
      index: ready.findIndex(
        (attachment) => attachment.imageId === item.attachment!.imageId,
      ),
    }))
    .filter((screen) => screen.index >= 0);
  if (ids.length === screens.length)
    registerOutboundScreensForImages(ids, screens);
}
